import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { supabase } from "../supabase";
import { render } from "@testing-library/react";

const { Title } = Typography;

const Lessons = ({ user, session }) => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [isPromptModalVisible, setIsPromptModalVisible] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    console.log("session", session);
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const { data, error } = await supabase.from("lessons").select("*");
      if (error) throw error;
      setLessons(data);
    } catch (error) {
      message.error("Error fetching lessons");
    } finally {
      setLoading(false);
    }
  };

  const showPromptModal = () => {
    setIsPromptModalVisible(true);
  };

  const handlePromptCancel = () => {
    setIsPromptModalVisible(false);
    setPrompt("");
  };

  const handlePromptSubmit = async () => {
    setIsPromptModalVisible(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-lesson`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            Authorization: `Bearer ` + session.access_token,
          },
          body: JSON.stringify({ prompt }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate lesson");
      }

      const newLesson = await response.json();
      setLessons((prevLessons) => [...prevLessons, newLesson]);
      message.success("Lesson generated successfully");
    } catch (error) {
      message.error("Error generating lesson");
    }
  };

  const showModal = (lesson) => {
    setEditingLesson(lesson);
    setIsModalVisible(true);
    if (lesson) {
      form.setFieldsValue(lesson);
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      if (editingLesson) {
        const { error } = await supabase
          .from("lessons")
          .update(values)
          .eq("id", editingLesson.id);
        if (error) throw error;
        message.success("Lesson updated successfully");
      } else {
        const { error } = await supabase.from("lessons").insert([values]);
        if (error) throw error;
        message.success("Lesson added successfully");
      }
      fetchLessons();
      handleCancel();
    } catch (error) {
      message.error("Error saving lesson");
    }
  };

  const deleteLesson = async (lessonId) => {
    try {
      const { error } = await supabase
        .from("lessons")
        .delete()
        .eq("id", lessonId);
      if (error) throw error;
      message.success("Lesson deleted successfully");
      fetchLessons();
    } catch (error) {
      message.error("Error deleting lesson");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <a href={`/lesson-content/${record.id}`}>{text}</a>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <>
          <div
            style={{
              display: "flex",
              gap: 8,
            }}
          >
            <Button onClick={() => showModal(record)}>
              <EditOutlined />
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this lesson?"
              onConfirm={() => deleteLesson(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </div>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Lessons</Title>
      <Modal
        title="Enter Prompt for Lesson Generation"
        visible={isPromptModalVisible}
        onCancel={handlePromptCancel}
        onOk={handlePromptSubmit}
      >
        <Form layout="vertical">
          <Form.Item
            label="Prompt"
            rules={[{ required: true, message: "Please input the prompt!" }]}
          >
            <Input.TextArea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Button
        type="default"
        onClick={showPromptModal}
        style={{ marginBottom: 16, marginRight: 8 }}
      >
        Generate Lesson
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => showModal()}
        style={{ marginBottom: 16 }}
      >
        Add Lesson
      </Button>
      <Table
        columns={columns}
        dataSource={lessons}
        loading={loading}
        rowKey="id"
      />
      <Modal
        title={editingLesson ? "Edit Lesson" : "Add Lesson"}
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={form.submit}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please input the title!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: "Please input the description!" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="duration"
            label="Duration (minutes)"
            rules={[{ required: true, message: "Please input the duration!" }]}
          >
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Lessons;
